import { useEffect, useState } from 'react';
import { Loader2, Plus, X, ShieldCheck, Building2, UserX, UserCheck, Pencil, KeyRound, UserPlus } from 'lucide-react';
import StaffForm from '../../components/admin/StaffForm';
import CredentialHandoff from '../../components/admin/CredentialHandoff';
import * as api from '../../lib/api';
import { useAuthStore } from '../../store/useAuthStore';
import { formatMonthYear } from '../../lib/format';

const ROLE_STYLES = {
  super_admin: 'bg-amber-100 text-amber-900',
  admin: 'bg-brand-100 text-brand-800',
  attendant: 'bg-sky-100 text-sky-800',
};

const ROLE_LABELS = { super_admin: 'Owner', admin: 'Admin', attendant: 'Attendant' };

/** Owner-only. An admin runs facilities; only an owner scopes staff. */
export default function StaffPage() {
  const canManage = useAuthStore((s) => s.permissions?.can_manage_staff);
  const [staff, setStaff] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);   // 'new' | staff row
  const [handoff, setHandoff] = useState(null);   // { email, password }

  const load = async () => {
    setState('loading');
    try {
      const [{ staff: list }, overview] = await Promise.all([api.adminStaff(), api.adminOverview()]);
      setStaff(list);
      setFacilities(overview.facilities);
      setState('ready');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  };

  useEffect(() => { if (canManage) load(); else setState('ready'); }, [canManage]);

  if (!canManage) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <ShieldCheck className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
        <p className="mt-3 text-sm font-bold text-slate-900">Owner access required</p>
        <p className="mt-1 text-sm text-slate-500">
          Only an owner can create or scope staff accounts.
        </p>
      </div>
    );
  }

  if (state === 'loading') {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>;
  }

  const act = async (fn) => {
    setError(null);
    try { await fn(); await load(); }
    catch (err) { setError(err.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Staff</h1>
          <p className="mt-1 text-sm text-slate-600">
            A staff member can only act on the facilities assigned here — the API returns 404 for any other.
          </p>
        </div>
        {!editing && (
          <button type="button" onClick={() => setEditing('new')}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-bold text-white hover:bg-brand-700">
            <UserPlus className="h-4 w-4" aria-hidden="true" /> Add staff
          </button>
        )}
      </div>

      {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      {editing && (
        <div className="mt-5">
          <StaffForm
            mode={editing === 'new' ? 'create' : 'edit'}
            initial={editing === 'new' ? undefined : editing}
            onCancel={() => setEditing(null)}
            onSubmit={async (form) => {
              if (editing === 'new') {
                const { user, temporary_password } = await api.adminCreateStaff(form);
                setHandoff({ email: user.email, password: temporary_password });
              } else {
                await api.adminUpdateUser(editing.public_id, form);
              }
              setEditing(null);
              await load();
            }}
          />
        </div>
      )}

      {handoff && (
        <CredentialHandoff email={handoff.email} password={handoff.password}
                           onClose={() => setHandoff(null)} />
      )}

      <ul className="mt-6 space-y-3">
        {staff.map((s) => (
          <li key={s.public_id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-extrabold text-slate-900">{s.first_name} {s.last_name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[12px] font-bold ${ROLE_STYLES[s.role]}`}>
                    {ROLE_LABELS[s.role]}
                  </span>
                  {s.status !== 'active' && (
                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[12px] font-bold text-rose-800">{s.status}</span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">
                  {s.email}
                  {s.last_login_at
                    ? ` · last signed in ${new Date(s.last_login_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                    : ' · never signed in'}
                </p>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {s.assignments.length === 0 && (
                    <span className="text-[12px] italic text-slate-400">
                      {s.role === 'super_admin' ? 'Unscoped — sees every facility' : 'No facility assigned'}
                    </span>
                  )}
                  {s.assignments.map((a) => (
                    <span key={a.facility_public_id}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 py-1 pl-2 pr-1 text-[12px] font-semibold text-slate-700">
                      <Building2 className="h-3 w-3 text-brand-500" aria-hidden="true" />
                      {a.name}
                      {a.is_primary && <span className="text-brand-700">·primary</span>}
                      <button type="button"
                              onClick={() => { setBusy(s.public_id); act(() => api.adminUnassignStaff(s.public_id, a.facility_public_id)); }}
                              aria-label={`Remove ${s.first_name} from ${a.name}`}
                              className="rounded-full p-0.5 text-slate-400 hover:bg-rose-100 hover:text-rose-700">
                        <X className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex shrink-0 gap-2">
                <button type="button" onClick={() => setEditing(s)} className="btn-ghost py-2 text-xs">
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit
                </button>
                {s.role !== 'super_admin' && (
                  <>
                    <button type="button" onClick={() => setAssigning(s)} className="btn-ghost py-2 text-xs">
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Assign
                    </button>
                    <button type="button" disabled={busy === s.public_id}
                            onClick={async () => {
                              // Deliberately confirm: this revokes every session
                              // they have and their current password stops working.
                              if (!window.confirm(
                                `Issue a new temporary password for ${s.first_name} ${s.last_name}?\n\n`
                                + 'Their current password stops working immediately and they will be '
                                + 'signed out everywhere.')) return;
                              setBusy(s.public_id);
                              try {
                                const r = await api.adminResetUserPassword(s.public_id);
                                setHandoff({ email: r.email, password: r.temporary_password });
                                await load();
                              } catch (err) { setError(err.message); } finally { setBusy(null); }
                            }}
                            className="btn-ghost py-2 text-xs">
                      <KeyRound className="h-3.5 w-3.5" aria-hidden="true" /> Reset password
                    </button>
                    <button type="button" disabled={busy === s.public_id}
                            onClick={() => { setBusy(s.public_id); act(() => api.adminSetStaffStatus(s.public_id, s.status === 'active' ? 'suspended' : 'active')); }}
                            className="btn-ghost py-2 text-xs">
                      {s.status === 'active'
                        ? <><UserX className="h-3.5 w-3.5" aria-hidden="true" /> Suspend</>
                        : <><UserCheck className="h-3.5 w-3.5" aria-hidden="true" /> Reinstate</>}
                    </button>
                  </>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {assigning && (
        <AssignDialog member={assigning} facilities={facilities}
                      onClose={() => setAssigning(null)}
                      onDone={() => { setAssigning(null); load(); }}
                      onError={setError} />
      )}
    </div>
  );
}

function AssignDialog({ member, facilities, onClose, onDone, onError }) {
  const assigned = new Set(member.assignments.map((a) => a.facility_public_id));
  const available = facilities.filter((f) => !assigned.has(f.public_id));
  const [facilityId, setFacilityId] = useState(available[0]?.public_id ?? '');
  const [isPrimary, setIsPrimary] = useState(member.assignments.length === 0);
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.adminAssignStaff(member.public_id, facilityId, isPrimary);
      onDone();
    } catch (err) {
      onError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" role="dialog" aria-modal="true"
         aria-label="Assign facility">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-extrabold text-slate-900">Assign {member.first_name}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {available.length === 0 ? (
          <p className="mb-5 text-sm text-slate-600">
            {member.first_name} is already assigned to every facility.
          </p>
        ) : (
          <>
            <label className="field-label mb-1" htmlFor="facility">Facility</label>
            <select id="facility" className="field-input mb-4" value={facilityId}
                    onChange={(e) => setFacilityId(e.target.value)}>
              {available.map((f) => <option key={f.public_id} value={f.public_id}>{f.name}</option>)}
            </select>

            <label className="mb-5 flex cursor-pointer items-center gap-2.5">
              <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)}
                     className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
              <span className="text-xs text-slate-700">Primary facility — where they normally work</span>
            </label>
          </>
        )}

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button type="submit" disabled={saving || available.length === 0} className="btn-primary flex-1">
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Assign
          </button>
        </div>
      </form>
    </div>
  );
}
